// src/components/Dashboard/CreateClaim.jsx
import React, { useState, useContext, useEffect } from 'react';
import toast from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import { LanguageContext } from '../../App';
import {
  createClaim,
  getLastInvoiceNumber,
  reserveNextInvoiceNumber,
  getGlobalSettings,
  saveGlobalSettings
} from '../../services/claimService';
import { getAdjusters, createAdjuster, updateAdjuster, deleteAdjuster } from '../../services/adjusterService';
import ConfirmModal from '../common/ConfirmModal';
import { formatDateToMMDDYYYY } from '../../utils/dateHelpers';
import './CreateClaim.css';

const CreateClaim = ({ onClaimCreated, duplicateFrom, onDuplicateConsumed }) => {
  const { language } = useContext(LanguageContext);
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [success, setSuccess] = useState(false);
  const [nextInvoiceNumber, setNextInvoiceNumber] = useState('089');
  const [manualInvoiceMode, setManualInvoiceMode] = useState(false);
  
  // Listas de configuración desde Firebase
  const [companiesList, setCompaniesList] = useState(['Adjustify']);
  // Ajustadores: fuente única en la colección `adjusters` (nombre + empresa +
  // foto/licencia/correo/acceso — ver adjusterService.js), no en config
  // global. Acá solo se necesita {id, name, company} para el selector y el
  // modal rápido de "agregar ajustador nuevo".
  const [adjustersList, setAdjustersList] = useState([]);
  const [causesList, setCausesList] = useState(['Water Damage', 'Fire Damage', 'Storm Damage', 'Theft', 'Other']);

  // Modales
  const [showCompanyConfigModal, setShowCompanyConfigModal] = useState(false);
  const [showAdjusterConfigModal, setShowAdjusterConfigModal] = useState(false);
  const [showCauseConfigModal, setShowCauseConfigModal] = useState(false);

  // Edición en modales
  const [editingItem, setEditingItem] = useState(null);
  const [newItemName, setNewItemName] = useState('');
  const [newAdjusterName, setNewAdjusterName] = useState('');
  const [editingAdjuster, setEditingAdjuster] = useState(null); // adjusterId, no nombre
  const [pendingDelete, setPendingDelete] = useState(null); // { type: 'company'|'adjuster'|'cause', name, id? }

  const [formData, setFormData] = useState({
    invoiceNumber: '',
    checkDate: '',
    companyName: 'Adjustify',
    payableTo: '',
    clientName: '',
    cause: '',
    adjusterName: '',
    claimNumber: '',
    crmClaimId: '',
    amount: '',
    adjusterPercentage: '20',
    adjusterCollected: '0',
    csrPercentage: '10',
    csrCollected: '0',
    totalCollected: '0'
  });

  // ============================================================
  // CARGA INICIAL DESDE FIREBASE
  // ============================================================
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        setInitialLoading(true);
        const lastNumber = await getLastInvoiceNumber();
        
        // CORRECCIÓN: Forzar que el invoiceNumber siempre tenga 3 dígitos (ej: 89 -> 089)
        const formattedInvoice = /^\d+$/.test(lastNumber) ? lastNumber.padStart(3, '0') : lastNumber;
        setNextInvoiceNumber(formattedInvoice);

        const [settings, allAdjusters] = await Promise.all([getGlobalSettings(), getAdjusters()]);
        setAdjustersList(allAdjusters);

        let defaultCompany = 'Adjustify';
        let defaultAdjuster = '';
        let defaultCause = 'Water Damage';

        if (settings) {
          if (settings.companiesList && settings.companiesList.length > 0) {
            setCompaniesList(settings.companiesList);
            defaultCompany = settings.companiesList[0];
          }
          if (settings.causesList && settings.causesList.length > 0) {
            setCausesList(settings.causesList);
            defaultCause = settings.causesList[0];
          }
        }

        const availableAdjusters = allAdjusters.filter(a => a.company === defaultCompany);
        if (availableAdjusters.length > 0) {
          defaultAdjuster = availableAdjusters[0].name;
        }

        setFormData(prev => ({ 
          ...prev, 
          invoiceNumber: formattedInvoice,
          companyName: defaultCompany,
          adjusterName: defaultAdjuster,
          cause: defaultCause
        }));

      } catch (error) {
        console.error('Error loading initial data from Firebase:', error);
      } finally {
        setInitialLoading(false);
      }
    };
    loadInitialData();
  }, []);

  // Copiar reclamo: mismo cliente/ajustador/empresa/causa/N° de reclamo —
  // invoice, fecha y monto del cheque quedan en blanco para el segundo
  // cheque. Se dispara cuando ClaimsList manda un duplicateFrom nuevo.
  useEffect(() => {
    if (!duplicateFrom || initialLoading) return;

    setFormData(prev => ({
      ...prev,
      companyName: duplicateFrom.companyName || prev.companyName,
      payableTo: duplicateFrom.payableTo || '',
      clientName: duplicateFrom.clientName || '',
      cause: duplicateFrom.cause || prev.cause,
      adjusterName: duplicateFrom.adjusterName || prev.adjusterName,
      claimNumber: duplicateFrom.claimNumber || '',
      crmClaimId: duplicateFrom.crmClaimId || '',
      adjusterPercentage: duplicateFrom.adjusterPercentage?.toString() || prev.adjusterPercentage,
      csrPercentage: duplicateFrom.csrPercentage?.toString() || prev.csrPercentage,
      checkDate: '',
      amount: '',
      adjusterCollected: '0',
      csrCollected: '0',
      totalCollected: '0'
    }));

    toast.success(language === 'es'
      ? `Copiado — completa el nuevo cheque para el reclamo ${duplicateFrom.claimNumber}`
      : `Copied — fill in the new check for claim ${duplicateFrom.claimNumber}`);

    if (onDuplicateConsumed) onDuplicateConsumed();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [duplicateFrom, initialLoading]);

  // Devuelve solo los nombres (para el <select>) — el modal de gestión
  // usa adjustersList filtrado directamente cuando necesita el id.
  const getAdjustersForCompany = (companyName) => {
    return adjustersList.filter(a => a.company === companyName).map(a => a.name);
  };

  const getAdjusterObjectsForCompany = (companyName) => {
    return adjustersList.filter(a => a.company === companyName);
  };

  const refreshAdjustersList = async () => {
    const data = await getAdjusters();
    setAdjustersList(data);
    return data;
  };

  // Cálculos financieros automáticos
  useEffect(() => {
    const amount = parseFloat(formData.amount) || 0;
    const adjusterPercent = parseFloat(formData.adjusterPercentage) || 0;
    const csrPercent = parseFloat(formData.csrPercentage) || 10;
    
    const adjusterCollected = (amount * adjusterPercent) / 100;
    const csrCollected = (adjusterCollected * csrPercent) / 100;
    const totalCollected = csrCollected;
    
    setFormData(prev => ({
      ...prev,
      adjusterCollected: adjusterCollected.toFixed(2),
      csrCollected: csrCollected.toFixed(2),
      totalCollected: totalCollected.toFixed(2)
    }));
  }, [formData.amount, formData.adjusterPercentage, formData.csrPercentage]);

  // Sincronizar ajustador al cambiar empresa
  useEffect(() => {
    if (initialLoading) return;
    const availableAdjusters = getAdjustersForCompany(formData.companyName);
    if (availableAdjusters.length > 0) {
      if (!availableAdjusters.includes(formData.adjusterName)) {
        setFormData(prev => ({ ...prev, adjusterName: availableAdjusters[0] }));
      }
    } else {
      setFormData(prev => ({ ...prev, adjusterName: '' }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.companyName]);

  // Handler de inputs controlado para evitar bloqueos.
  // checkDate usa <input type="date"> (ver JSX) — el navegador entrega
  // siempre YYYY-MM-DD válido, sin la ambigüedad de la máscara de texto
  // libre que antes dejaba guardar fechas corruptas (ej. "75/20/26").
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const toggleManualInvoice = () => {
    setManualInvoiceMode(!manualInvoiceMode);
    setFormData(prev => ({ ...prev, invoiceNumber: nextInvoiceNumber }));
  };

  const handleManualInvoiceChange = (e) => {
    const value = e.target.value;
    if (value === '' || /^\d+$/.test(value)) {
      setFormData(prev => ({ ...prev, invoiceNumber: value }));
    }
  };

  // Forzar ceros en el invoice si el usuario lo digita manualmente y quita el foco
  const handleManualInvoiceBlur = (e) => {
    if (!manualInvoiceMode) return;
    const value = e.target.value.trim();
    if (value && /^\d+$/.test(value)) {
      setFormData(prev => ({ ...prev, invoiceNumber: value.padStart(3, '0') }));
    }
  };

  const updateConfigInFirebase = async (updatedCompanies, updatedCauses) => {
    try {
      await saveGlobalSettings({
        companiesList: updatedCompanies || companiesList,
        causesList: updatedCauses || causesList
      });
    } catch (error) {
      console.error("Error saving settings to Firebase:", error);
    }
  };

  // ============================================================
  // OPERACIONES DE LOS MODALES (FIREBASE HOOKS)
  // ============================================================
  const handleAddCompany = async () => {
    if (!newItemName.trim() || companiesList.includes(newItemName.trim())) return;
    const name = newItemName.trim();
    const updatedCompanies = [...companiesList, name];
    setCompaniesList(updatedCompanies);
    setNewItemName('');
    await updateConfigInFirebase(updatedCompanies, null);
  };

  const handleSaveCompanyEdit = async () => {
    if (!editingItem || !newItemName.trim()) return;
    const oldName = editingItem; const newName = newItemName.trim();
    if (oldName !== newName && companiesList.includes(newName)) return;
    const updatedCompanies = companiesList.map(c => c === oldName ? newName : c);
    setCompaniesList(updatedCompanies);
    if (formData.companyName === oldName) setFormData(prev => ({ ...prev, companyName: newName }));
    setEditingItem(null); setNewItemName('');
    await updateConfigInFirebase(updatedCompanies, null);
  };

  const handleDeleteCompany = (companyName) => {
    if (companyName === 'Adjustify' || companiesList.length <= 1) return;
    setPendingDelete({ type: 'company', name: companyName });
  };

  const performDeleteCompany = async (companyName) => {
    const updatedCompanies = companiesList.filter(c => c !== companyName);
    setCompaniesList(updatedCompanies);
    if (formData.companyName === companyName) setFormData(prev => ({ ...prev, companyName: updatedCompanies[0] || '' }));
    await updateConfigInFirebase(updatedCompanies, null);
  };

  // Ajustadores: agregar acá crea el registro completo en `adjusters`
  // (solo nombre + empresa por ahora — foto/licencia/correo/acceso se
  // completan luego desde la pestaña "Ajustadores"). Editar el nombre solo
  // cambia el registro hacia adelante, no los reclamos ya creados con el
  // nombre anterior (quedan como texto histórico).
  const handleAddAdjuster = async () => {
    if (!newAdjusterName.trim()) return;
    const name = newAdjusterName.trim(); const company = formData.companyName;
    if (getAdjustersForCompany(company).includes(name)) return;
    try {
      await createAdjuster({ name, company, license: '', photoDataUrl: '', email: '', notes: '' });
      setNewAdjusterName('');
      await refreshAdjustersList();
    } catch (error) {
      console.error('Error creating adjuster:', error);
      toast.error(language === 'es' ? 'Error al agregar el ajustador' : 'Error adding adjuster');
    }
  };

  const handleSaveAdjusterEdit = async () => {
    if (!editingAdjuster || !newAdjusterName.trim()) return;
    const newName = newAdjusterName.trim();
    const oldAdjuster = adjustersList.find(a => a.id === editingAdjuster);
    try {
      await updateAdjuster(editingAdjuster, { name: newName });
      if (oldAdjuster && formData.adjusterName === oldAdjuster.name) {
        setFormData(prev => ({ ...prev, adjusterName: newName }));
      }
      setEditingAdjuster(null); setNewAdjusterName('');
      await refreshAdjustersList();
    } catch (error) {
      console.error('Error updating adjuster:', error);
      toast.error(language === 'es' ? 'Error al editar el ajustador' : 'Error editing adjuster');
    }
  };

  const handleDeleteAdjuster = (adjuster) => {
    setPendingDelete({ type: 'adjuster', name: adjuster.name, id: adjuster.id });
  };

  const performDeleteAdjuster = async (adjusterId, adjusterName) => {
    try {
      await deleteAdjuster(adjusterId);
      if (formData.adjusterName === adjusterName) {
        const remaining = getAdjustersForCompany(formData.companyName).filter(n => n !== adjusterName);
        setFormData(prev => ({ ...prev, adjusterName: remaining[0] || '' }));
      }
      await refreshAdjustersList();
    } catch (error) {
      console.error('Error deleting adjuster:', error);
      toast.error(language === 'es' ? 'Error al eliminar el ajustador' : 'Error deleting adjuster');
    }
  };

  const handleAddCause = async () => {
    if (!newItemName.trim() || causesList.includes(newItemName.trim())) return;
    const updatedCauses = [...causesList, newItemName.trim()];
    setCausesList(updatedCauses); setNewItemName('');
    await updateConfigInFirebase(null, updatedCauses);
  };

  const handleSaveCauseEdit = async () => {
    if (!editingItem || !newItemName.trim()) return;
    const updatedCauses = causesList.map(c => c === editingItem ? newItemName.trim() : c);
    setCausesList(updatedCauses);
    if (formData.cause === editingItem) setFormData(prev => ({ ...prev, cause: newItemName.trim() }));
    setEditingItem(null); setNewItemName('');
    await updateConfigInFirebase(null, updatedCauses);
  };

  const handleDeleteCause = (causeName) => {
    if (causesList.length <= 1) return;
    setPendingDelete({ type: 'cause', name: causeName });
  };

  const performDeleteCause = async (causeName) => {
    const updatedCauses = causesList.filter(c => c !== causeName);
    setCausesList(updatedCauses);
    if (formData.cause === causeName) setFormData(prev => ({ ...prev, cause: updatedCauses[0] || '' }));
    await updateConfigInFirebase(null, updatedCauses);
  };

  const handleConfirmDelete = async () => {
    if (!pendingDelete) return;
    const { type, name, id } = pendingDelete;
    if (type === 'company') await performDeleteCompany(name);
    if (type === 'adjuster') await performDeleteAdjuster(id, name);
    if (type === 'cause') await performDeleteCause(name);
    setPendingDelete(null);
  };

  const deleteConfirmCopy = () => {
    if (!pendingDelete) return { title: '', message: '' };
    const labels = {
      company: language === 'es' ? 'la empresa' : 'company',
      adjuster: language === 'es' ? 'al ajustador' : 'adjuster',
      cause: language === 'es' ? 'la causa' : 'cause'
    };
    return {
      title: language === 'es' ? 'Confirmar eliminación' : 'Confirm deletion',
      message: language === 'es'
        ? `¿Eliminar ${labels[pendingDelete.type]} "${pendingDelete.name}"?`
        : `Delete ${labels[pendingDelete.type]} "${pendingDelete.name}"?`
    };
  };

  // ============================================================
  // SUBMIT DEL RECLAMO
  // ============================================================
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      // En modo automático se reserva el número justo antes de crear (no
      // el que se precargó al abrir el formulario) — así dos reclamos
      // creados casi al mismo tiempo no pueden chocar en el mismo
      // invoiceNumber y perder silenciosamente su deuda de operador.
      const invoiceNumberToUse = manualInvoiceMode
        ? formData.invoiceNumber
        : await reserveNextInvoiceNumber();

      const claimData = {
        invoiceNumber: invoiceNumberToUse,
        // El input es type="date" (YYYY-MM-DD) — se convierte al formato
        // canónico MM/DD/YYYY que usa el resto de la app antes de guardar.
        checkDate: formatDateToMMDDYYYY(formData.checkDate),
        companyName: formData.companyName,
        payableTo: formData.payableTo,
        clientName: formData.clientName,
        cause: formData.cause,
        adjusterName: formData.adjusterName,
        claimNumber: formData.claimNumber, // Texto libre alfanumérico del CRM
        crmClaimId: formData.crmClaimId,
        amount: parseFloat(formData.amount),
        adjusterPercentage: parseFloat(formData.adjusterPercentage),
        adjusterCollected: parseFloat(formData.adjusterCollected),
        csrPercentage: parseFloat(formData.csrPercentage),
        csrCollected: parseFloat(formData.csrCollected),
        totalCollected: parseFloat(formData.totalCollected),
        deposit: 0,
        pending: parseFloat(formData.totalCollected)
      };

      await createClaim(claimData, currentUser.email);

      // Auto-incremento formateado para el siguiente Invoice (ej: 089 -> 090)
      const nextNumber = (parseInt(invoiceNumberToUse, 10) + 1).toString().padStart(3, '0');
      setNextInvoiceNumber(nextNumber);

      setFormData(prev => ({
        ...prev,
        invoiceNumber: manualInvoiceMode ? formData.invoiceNumber : nextNumber,
        checkDate: '',
        payableTo: '',
        clientName: '',
        claimNumber: '',
        crmClaimId: '',
        amount: '',
        adjusterPercentage: '20',
        adjusterCollected: '0',
        csrPercentage: '10',
        csrCollected: '0',
        totalCollected: '0'
      }));
      
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
      if (onClaimCreated) onClaimCreated();
    } catch (error) {
      console.error(error);
      toast.error(language === 'es' ? 'Error al crear el reclamo' : 'Error creating claim');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (formData.clientName && formData.adjusterName && formData.companyName) {
      setFormData(prev => {
        if (prev.payableTo) return prev;
        return { ...prev, payableTo: `${prev.clientName} & ${prev.adjusterName} & ${prev.companyName}` };
      });
    }
  }, [formData.clientName, formData.adjusterName, formData.companyName]);

  if (initialLoading) {
    return (
      <div className="cc-loading-wrapper">
        <i className="fa-solid fa-spinner fa-spin"></i>
        <p>{language === 'es' ? 'Cargando configuraciones de Firebase...' : 'Loading configurations from Firebase...'}</p>
      </div>
    );
  }

  return (
    <div className="cc-wrapper">
      <div className="cc-header">
        <div className="cc-header-left">
          <h2><i className="fa-solid fa-file-circle-plus"></i> {language === 'es' ? 'Nuevo Reclamo' : 'New Claim'}</h2>
          <p>{language === 'es' ? 'Complete la información del reclamo' : 'Complete claim information'}</p>
        </div>
      </div>

      {success && (
        <div className="cc-success">
          <i className="fa-solid fa-circle-check"></i> {language === 'es' ? '¡Reclamo creado exitosamente!' : 'Claim created successfully!'}
        </div>
      )}

      <form onSubmit={handleSubmit} className="cc-form">
        <div className="cc-grid">
          <div className="cc-input-group">
            <label className="cc-label"><i className="fa-solid fa-hashtag"></i> {language === 'es' ? 'Número de Invoice' : 'Invoice Number'} *</label>
            <div className="cc-invoice-input-wrapper">
              <input
                type="text"
                name="invoiceNumber"
                value={formData.invoiceNumber}
                onChange={manualInvoiceMode ? handleManualInvoiceChange : undefined}
                onBlur={handleManualInvoiceBlur}
                readOnly={!manualInvoiceMode}
                disabled={!manualInvoiceMode}
                className={`cc-input cc-input-invoice ${manualInvoiceMode ? 'cc-input-manual' : ''}`}
                placeholder="ej. 089"
              />
              <button type="button" onClick={toggleManualInvoice} className={`cc-btn-toggle-manual ${manualInvoiceMode ? 'active' : ''}`}>
                <i className={`fa-solid ${manualInvoiceMode ? 'fa-rotate-left' : 'fa-pencil'}`}></i>
              </button>
            </div>
          </div>
          
          <div className="cc-input-group">
            <label className="cc-label"><i className="fa-solid fa-calendar-day"></i> {language === 'es' ? 'Fecha del Cheque' : 'Check Date'} *</label>
            <input type="date" name="checkDate" value={formData.checkDate} onChange={handleChange} required className="cc-input" />
          </div>
        </div>

        <div className="cc-grid">
          <div className="cc-input-group">
            <label className="cc-label"><i className="fa-solid fa-building"></i> {language === 'es' ? 'Empresa' : 'Company'} *</label>
            <div className="cc-select-with-config">
              <select name="companyName" value={formData.companyName} onChange={handleChange} required className="cc-select">
                {companiesList.map((company, idx) => (
                  <option key={idx} value={company}>{company}</option>
                ))}
              </select>
              <button type="button" onClick={() => { setEditingItem(null); setNewItemName(''); setShowCompanyConfigModal(true); }} className="cc-btn-config"><i className="fa-solid fa-gear"></i></button>
            </div>
          </div>
          
          <div className="cc-input-group">
            <label className="cc-label"><i className="fa-solid fa-user"></i> {language === 'es' ? 'Cliente' : 'Client'} *</label>
            <input type="text" name="clientName" value={formData.clientName} onChange={handleChange} required className="cc-input" placeholder="Full client name" />
          </div>
        </div>

        <div className="cc-grid">
          <div className="cc-input-group">
            <label className="cc-label"><i className="fa-solid fa-triangle-exclamation"></i> {language === 'es' ? 'Causa' : 'Cause'} *</label>
            <div className="cc-select-with-config">
              <select name="cause" value={formData.cause} onChange={handleChange} required className="cc-select">
                <option value="">{language === 'es' ? 'Seleccione una causa' : 'Select a cause'}</option>
                {causesList.map((cause, idx) => (
                  <option key={idx} value={cause}>{cause}</option>
                ))}
              </select>
              <button type="button" onClick={() => { setEditingItem(null); setNewItemName(''); setShowCauseConfigModal(true); }} className="cc-btn-config"><i className="fa-solid fa-gear"></i></button>
            </div>
          </div>
          
          <div className="cc-input-group">
            <label className="cc-label"><i className="fa-solid fa-user-tie"></i> {language === 'es' ? 'Ajustador' : 'Adjuster'} *</label>
            <div className="cc-select-with-config">
              <select name="adjusterName" value={formData.adjusterName} onChange={handleChange} required className="cc-select" disabled={getAdjustersForCompany(formData.companyName).length === 0}>
                <option value="">{getAdjustersForCompany(formData.companyName).length === 0 ? 'No adjusters' : 'Select an adjuster'}</option>
                {getAdjustersForCompany(formData.companyName).map((adjuster, idx) => (
                  <option key={idx} value={adjuster}>{adjuster}</option>
                ))}
              </select>
              <button type="button" onClick={() => { setEditingAdjuster(null); setNewAdjusterName(''); setShowAdjusterConfigModal(true); }} className="cc-btn-config"><i className="fa-solid fa-gear"></i></button>
            </div>
          </div>
        </div>

        <div className="cc-grid">
          <div className="cc-input-group">
            <label className="cc-label"><i className="fa-solid fa-hashtag"></i> {language === 'es' ? 'Número de Reclamo' : 'Claim Number'} *</label>
            <input type="text" name="claimNumber" value={formData.claimNumber} onChange={handleChange} required className="cc-input" placeholder="ej. CDP00202667" />
          </div>
          
          <div className="cc-input-group">
            <label className="cc-label"><i className="fa-solid fa-receipt"></i> {language === 'es' ? 'Payable To' : 'Payable To'} *</label>
            <input type="text" name="payableTo" value={formData.payableTo} onChange={handleChange} required className="cc-input" placeholder="Exact name on check" />
          </div>
        </div>

        <div className="cc-grid">
          <div className="cc-input-group cc-full-width">
            <label className="cc-label"><i className="fa-solid fa-link"></i> {language === 'es' ? 'ID del Reclamo (CRM)' : 'Claim ID (CRM)'} *</label>
            <input type="text" name="crmClaimId" value={formData.crmClaimId} onChange={handleChange} required placeholder="Ej: 53063" className="cc-input" />
          </div>
        </div>

        <div className="cc-grid">
          <div className="cc-input-group">
            <label className="cc-label"><i className="fa-solid fa-dollar-sign"></i> {language === 'es' ? 'Monto del Cheque' : 'Check Amount'} *</label>
            <input type="number" name="amount" value={formData.amount} onChange={handleChange} required step="0.01" placeholder="0.00" className="cc-input cc-input-amount" />
          </div>
          
          <div className="cc-input-group">
            <label className="cc-label"><i className="fa-solid fa-percent"></i> {language === 'es' ? '% Ajustador' : 'Adjuster %'} *</label>
            <input type="number" name="adjusterPercentage" value={formData.adjusterPercentage} onChange={handleChange} required step="0.1" min="0" max="100" className="cc-input cc-input-percent" />
          </div>
        </div>

        {/* Panel de cálculos */}
        <div className="cc-calc-panel">
          <h3 className="cc-panel-title"><i className="fa-solid fa-calculator"></i> {language === 'es' ? 'Cálculos Automáticos' : 'Automatic Calculations'}</h3>
          <div className="cc-calc-grid">
            <div className="cc-calc-card">
              <span className="cc-calc-label"><i className="fa-solid fa-user-tie"></i> {language === 'es' ? 'Ajustador Collected' : 'Adjuster Collected'}</span>
              <span className="cc-calc-value cc-calc-adjuster">${parseFloat(formData.adjusterCollected).toLocaleString()}</span>
              <span className="cc-calc-formula">({formData.amount || 0} × {formData.adjusterPercentage || 0}%)</span>
            </div>

            <div className="cc-calc-card">
              <span className="cc-calc-label"><i className="fa-solid fa-headset"></i> {language === 'es' ? '% CSR' : 'CSR %'}</span>
              <div className="cc-csr-percent-input">
                <input type="number" name="csrPercentage" value={formData.csrPercentage} onChange={handleChange} step="0.1" min="0" max="100" className="cc-mini-input" />
                <span className="cc-percent-symbol">%</span>
              </div>
            </div>

            <div className="cc-calc-card">
              <span className="cc-calc-label"><i className="fa-solid fa-coins"></i> {language === 'es' ? 'CSR Collected' : 'CSR Collected'}</span>
              <span className="cc-calc-value cc-calc-csr">${parseFloat(formData.csrCollected).toLocaleString()}</span>
              <span className="cc-calc-formula">(${parseFloat(formData.adjusterCollected).toLocaleString()} × {formData.csrPercentage || 10}%)</span>
            </div>

            <div className="cc-calc-card cc-calc-total-card">
              <span className="cc-calc-label"><i className="fa-solid fa-chart-line"></i> {language === 'es' ? 'Total a Cobrar' : 'Total to Collect'}</span>
              <span className="cc-calc-value cc-calc-total">${parseFloat(formData.totalCollected).toLocaleString()}</span>
              <span className="cc-calc-formula">{language === 'es' ? 'CSR Collected' : 'CSR Collected'}</span>
            </div>
          </div>
        </div>

        {formData.invoiceNumber && formData.clientName && formData.claimNumber && formData.amount && (
          <div className="cc-note-preview">
            <h3 className="cc-panel-title"><i className="fa-solid fa-note-sticky"></i> {language === 'es' ? 'Nota que se generará en el CRM' : 'Note to be generated in CRM'}</h3>
            <div className="cc-note-content">
              <div className="cc-note-line"><strong>Client:</strong> {formData.clientName}</div>
              <div className="cc-note-line"><strong>Payable to:</strong> {formData.payableTo}</div>
              <div className="cc-note-line"><strong>Claim Number:</strong> {formData.claimNumber}</div>
              <div className="cc-note-line cc-note-highlight"><strong>Check Amount:</strong> ${parseFloat(formData.amount).toLocaleString()}</div>
            </div>
          </div>
        )}

        <div className="cc-form-footer">
          <button type="submit" className="cc-btn-submit" disabled={loading}>
            {loading ? <><i className="fa-solid fa-spinner fa-spin"></i> ...</> : <><i className="fa-solid fa-save"></i> {language === 'es' ? 'Guardar Reclamo' : 'Save Claim'}</>}
          </button>
        </div>
      </form>

      {/* ============================================================
          MODALES DE CONFIGURACIÓN DIRECTOS A FIREBASE CLOUD
          ============================================================ */}
      {showCompanyConfigModal && (
        <div className="cc-modal-overlay" onClick={() => setShowCompanyConfigModal(false)}>
          <div className="cc-modal-content cc-modal-company" onClick={(e) => e.stopPropagation()}>
            <div className="cc-modal-header">
              <h3><i className="fa-solid fa-building"></i> {language === 'es' ? 'Configurar Empresas' : 'Configure Companies'}</h3>
              <button className="cc-modal-close" onClick={() => setShowCompanyConfigModal(false)}><i className="fa-solid fa-times"></i></button>
            </div>
            <div className="cc-modal-body">
              <div className="cc-config-list">
                {companiesList.map(c => (
                  <div key={c} className="cc-config-item">
                    {editingItem === c ? (
                      <div className="cc-config-edit">
                        <input type="text" value={newItemName} onChange={e => setNewItemName(e.target.value)} className="cc-modal-input cc-config-input" autoFocus />
                        <button type="button" onClick={handleSaveCompanyEdit} className="cc-config-btn-save"><i className="fa-solid fa-check"></i></button>
                        <button type="button" onClick={() => setEditingItem(null)} className="cc-config-btn-cancel"><i className="fa-solid fa-times"></i></button>
                      </div>
                    ) : (
                      <>
                        <span className="cc-config-item-name">{c}</span>
                        <div className="cc-config-item-actions">
                          <button type="button" onClick={() => { setEditingItem(c); setNewItemName(c); }} className="cc-config-btn-edit"><i className="fa-solid fa-pen"></i></button>
                          <button type="button" onClick={() => handleDeleteCompany(c)} className="cc-config-btn-delete" disabled={c === 'Adjustify' || companiesList.length <= 1}><i className="fa-solid fa-trash"></i></button>
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
              {!editingItem && (
                <div className="cc-config-add">
                  <input type="text" value={newItemName} onChange={e => setNewItemName(e.target.value)} placeholder="New company..." className="cc-modal-input cc-config-input" />
                  <button type="button" onClick={handleAddCompany} className="cc-config-btn-add"><i className="fa-solid fa-plus"></i> {language === 'es' ? 'Agregar' : 'Add'}</button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {showAdjusterConfigModal && (
        <div className="cc-modal-overlay" onClick={() => setShowAdjusterConfigModal(false)}>
          <div className="cc-modal-content cc-modal-adjuster" onClick={(e) => e.stopPropagation()}>
            <div className="cc-modal-header">
              <h3><i className="fa-solid fa-user-tie"></i> {language === 'es' ? 'Configurar Ajustadores' : 'Configure Adjusters'}</h3>
              <button className="cc-modal-close" onClick={() => setShowAdjusterConfigModal(false)}><i className="fa-solid fa-times"></i></button>
            </div>
            <div className="cc-modal-body">
              <div className="cc-config-company-selector">
                <label>Company:</label>
                <select value={formData.companyName} onChange={(e) => setFormData(prev => ({ ...prev, companyName: e.target.value }))} className="cc-select cc-config-select">
                  {companiesList.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div className="cc-config-list">
                {getAdjusterObjectsForCompany(formData.companyName).map(a => (
                  <div key={a.id} className="cc-config-item">
                    {editingAdjuster === a.id ? (
                      <div className="cc-config-edit">
                        <input type="text" value={newAdjusterName} onChange={e => setNewAdjusterName(e.target.value)} className="cc-modal-input cc-config-input" autoFocus />
                        <button type="button" onClick={handleSaveAdjusterEdit} className="cc-config-btn-save"><i className="fa-solid fa-check"></i></button>
                        <button type="button" onClick={() => setEditingAdjuster(null)} className="cc-config-btn-cancel"><i className="fa-solid fa-times"></i></button>
                      </div>
                    ) : (
                      <>
                        <span className="cc-config-item-name">{a.name}</span>
                        <div className="cc-config-item-actions">
                          <button type="button" onClick={() => { setEditingAdjuster(a.id); setNewAdjusterName(a.name); }} className="cc-config-btn-edit"><i className="fa-solid fa-pen"></i></button>
                          <button type="button" onClick={() => handleDeleteAdjuster(a)} className="cc-config-btn-delete" disabled={getAdjustersForCompany(formData.companyName).length <= 1}><i className="fa-solid fa-trash"></i></button>
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
              {!editingAdjuster && (
                <div className="cc-config-add">
                  <input type="text" value={newAdjusterName} onChange={e => setNewAdjusterName(e.target.value)} placeholder="New adjuster..." className="cc-modal-input cc-config-input" />
                  <button type="button" onClick={handleAddAdjuster} className="cc-config-btn-add"><i className="fa-solid fa-plus"></i> {language === 'es' ? 'Agregar' : 'Add'}</button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {showCauseConfigModal && (
        <div className="cc-modal-overlay" onClick={() => setShowCauseConfigModal(false)}>
          <div className="cc-modal-content cc-modal-cause" onClick={(e) => e.stopPropagation()}>
            <div className="cc-modal-header">
              <h3><i className="fa-solid fa-triangle-exclamation"></i> {language === 'es' ? 'Configurar Causas' : 'Configure Causes'}</h3>
              <button className="cc-modal-close" onClick={() => setShowCauseConfigModal(false)}><i className="fa-solid fa-times"></i></button>
            </div>
            <div className="cc-modal-body">
              <div className="cc-config-list">
                {causesList.map(c => (
                  <div key={c} className="cc-config-item">
                    {editingItem === c ? (
                      <div className="cc-config-edit">
                        <input type="text" value={newItemName} onChange={e => setNewItemName(e.target.value)} className="cc-modal-input cc-config-input" autoFocus />
                        <button type="button" onClick={handleSaveCauseEdit} className="cc-config-btn-save"><i className="fa-solid fa-check"></i></button>
                        <button type="button" onClick={() => setEditingItem(null)} className="cc-config-btn-cancel"><i className="fa-solid fa-times"></i></button>
                      </div>
                    ) : (
                      <>
                        <span className="cc-config-item-name">{c}</span>
                        <div className="cc-config-item-actions">
                          <button type="button" onClick={() => { setEditingItem(c); setNewItemName(c); }} className="cc-config-btn-edit"><i className="fa-solid fa-pen"></i></button>
                          <button type="button" onClick={() => handleDeleteCause(c)} className="cc-config-btn-delete" disabled={causesList.length <= 1}><i className="fa-solid fa-trash"></i></button>
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
              {!editingItem && (
                <div className="cc-config-add">
                  <input type="text" value={newItemName} onChange={e => setNewItemName(e.target.value)} placeholder="New cause..." className="cc-modal-input cc-config-input" />
                  <button type="button" onClick={handleAddCause} className="cc-config-btn-add"><i className="fa-solid fa-plus"></i> {language === 'es' ? 'Agregar' : 'Add'}</button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        open={!!pendingDelete}
        icon="fa-trash"
        danger
        title={deleteConfirmCopy().title}
        message={deleteConfirmCopy().message}
        confirmLabel={language === 'es' ? 'Eliminar' : 'Delete'}
        cancelLabel={language === 'es' ? 'Cancelar' : 'Cancel'}
        onConfirm={handleConfirmDelete}
        onCancel={() => setPendingDelete(null)}
      />
    </div>
  );
};

export default CreateClaim;